<template>
  <div class="space-y-4 @container max-w-6xl mx-auto">
    <div>
      <div
        v-if="!domain"
        class="bg-white flex flex-col @lg:flex-row gap-2 justify-between items-start @lg:items-end"
      >
        <h2 class="text-5xl font-semibold text-gray-700">Dashboard Stats</h2>
        <StatsTimeRangePicker v-model:start="start" v-model:end="end" />
      </div>
      <div v-if="!domain" class="w-full mt-2 grid @md:grid-cols-2 gap-4">
        <StatBlock
          label="Refreshed websites"
          :stat="updatedWebsiteCount"
          :loading="updatedWebsiteCountLoading"
        />
        <StatBlock
          label="Currently pending suggestions"
          :stat="pendingSuggestionsCount"
          :loading="statusCountsLoading"
        />
      </div>
    </div>
    <div>
      <div
        class="flex flex-col @md:flex-row gap-2 justify-between items-start @md:items-end"
      >
        <h2 class="text-2xl font-semibold text-gray-700">Suggestion History</h2>
        <StatsTimeRangePicker
          v-if="domain"
          v-model:start="start"
          v-model:end="end"
        />
      </div>
      <div
        class="mt-2 border-2 border-gray-200 p-4 rounded-xl h-96 flex flex-col items-center gap-4"
      >
        <div
          class="font-semibold uppercase leading-none tracking-wider text-gray-600"
        >
          Number Rejected, Accepted, and Created by Day
        </div>
        <SuggestionHistoryChart
          class="w-full"
          :stats="historyChartData"
          :loading="historyChartDataLoading"
        />
      </div>
    </div>
    <div>
      <h2>
        <span class="text-2xl font-semibold text-gray-700"
          >Suggestion Stats</span
        ><span class="ml-1 text-xs font-semibold text-gray-400">
          for {{ startPretty }} - {{ endPretty }}</span
        >
      </h2>
      <div class="mt-2">
        <div class="grid grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-12 gap-4">
          <StatBlock
            class="@3xl:col-span-3"
            label="Total Created"
            :stat="createdSuggestionsCount"
            :loading="statusCountsLoading"
          />
          <StatBlock
            class="@3xl:col-span-3"
            label="Total Accepted"
            :stat="
              uneditedAcceptedSuggestionsCount + editedAcceptedSuggestionsCount
            "
            :loading="statusCountsLoading"
          />
          <StatBlock
            class="@3xl:col-span-3"
            label="Total Rejected"
            :stat="rejectedSuggestionsCount"
            :loading="statusCountsLoading"
          />
          <StatBlock
            class="@3xl:col-span-3"
            label="Percent Rejected"
            :stat="percentRejected"
            :loading="statusCountsLoading"
          />
          <StatBlock
            class="@3xl:col-span-3"
            label="Accepted (no edits)"
            :stat="uneditedAcceptedSuggestionsCount"
            :loading="statusCountsLoading"
          />
          <StatBlock
            class="@3xl:col-span-3"
            label="Accepted (with edits)"
            :stat="editedAcceptedSuggestionsCount"
            :loading="statusCountsLoading"
          />
          <div
            class="@md:col-span-2 @3xl:col-span-6 @3xl:row-span-3 border-2 border-gray-200 rounded-xl p-4 flex flex-col items-center gap-8"
          >
            <div
              class="font-semibold uppercase leading-none tracking-wider text-gray-600"
            >
              Rejection Reason by Category
            </div>
            <div class="w-full flex flex-col justify-center grow pb-8">
              <RejectReasonChart
                :stats="rejectReasons"
                :loading="rejectReasonsLoading"
              />
            </div>
          </div>
          <StatBlock
            class="@md:col-span-2 @3xl:col-span-6"
            label="Percent edited before accepting"
            :stat="percentEdited"
            :loading="statusCountsLoading"
          />
          <StatBlock
            class="@md:col-span-2 @3xl:col-span-6 @container"
            label="Average time to review"
            :loading="avgReviewTimeLoading"
          >
            <div
              class="flex flex-col @xs:flex-row gap-2 @xs:gap-4 justify-center"
            >
              <div>
                <span class="text-5xl font-bold">
                  {{ avgReviewTime?.days }}
                </span>
                <span class="ml-1 text-sm text-gray-500">{{
                  avgReviewTime?.days === 1 ? "Day" : "Days"
                }}</span>
              </div>
              <div>
                <span class="text-5xl font-bold">
                  {{ avgReviewTime?.hours }}
                </span>
                <span class="ml-1 text-sm text-gray-500">{{
                  avgReviewTime?.hours === 1 ? "Hour" : "Hours"
                }}</span>
              </div>
              <div>
                <span class="text-5xl font-bold">
                  {{ avgReviewTime?.minutes }}
                </span>
                <span class="ml-1 text-sm text-gray-500">{{
                  avgReviewTime?.minutes === 1 ? "Minute" : "Minutes"
                }}</span>
              </div>
            </div>
          </StatBlock>
        </div>
      </div>
    </div>
    <div v-if="!props.domain">
      <h2>
        <span class="text-2xl font-semibold text-gray-700">Agent Stats</span>
      </h2>
      <div class="mt-2 grid @2xl:grid-cols-3 gap-4">
        <StatBlock
          label="Enrolled"
          :stat="agentsEnrolledCount"
          :loading="agentCountsLoading"
        />
        <StatBlock
          label="Granted Permission"
          :stat="agentsPermissionGrantedCount"
          :loading="agentCountsLoading"
        />
        <StatBlock
          label="Percent Granted Permission"
          :stat="agentsPercentPermissionGranted"
          :loading="agentCountsLoading"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import RejectReasonChart from "@/components/charts/RejectReasonChart.vue";
import SuggestionHistoryChart from "@/components/charts/SuggestionHistoryChart.vue";
import StatBlock from "@/components/StatBlock.vue";
import StatsTimeRangePicker from "@/components/StatsTimeRangePicker.vue";

const route = useRoute();

const props = defineProps({
  domain: { type: String, required: false },
});

const stats = ref(null);

const start = ref("");
const end = ref("");

const updatedWebsiteCount = ref(null);
const updatedWebsiteCountLoading = ref(true);

const pendingSuggestionsCount = ref(null);
const createdSuggestionsCount = ref(null);
const uneditedAcceptedSuggestionsCount = ref(null);
const editedAcceptedSuggestionsCount = ref(null);
const acceptedSuggestionsCount = ref(null);
const rejectedSuggestionsCount = ref(null);
const agentsEnrolledCount = ref(null);
const agentsPermissionGrantedCount = ref(null);
const statusCountsLoading = ref(true);
const agentCountsLoading = ref(true);

const avgReviewTimeSeconds = ref(null);
const avgReviewTimeLoading = ref(true);

const rejectReasons = ref(null);
const rejectReasonsLoading = ref(true);

const historyChartData = ref(null);
const historyChartDataLoading = ref(true);

const vendor = computed(() => route.params.vendor);

const startPretty = computed(() => {
  const [year, month, day] = start.value.split("-");
  const startDate = new Date(year, month - 1, day);
  return startDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
});

const endPretty = computed(() => {
  const [year, month, day] = end.value.split("-");
  const endDate = new Date(year, month - 1, day);
  return endDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
});

const totalReviewedCount = computed(
  () =>
    uneditedAcceptedSuggestionsCount.value +
    editedAcceptedSuggestionsCount.value +
    rejectedSuggestionsCount.value,
);

const percentEdited = computed(() => {
  if (totalReviewedCount.value === 0) {
    return "N/A";
  }
  const percentage =
    editedAcceptedSuggestionsCount.value / totalReviewedCount.value;
  return `${Math.round(percentage * 1000) / 10}%`;
});

const percentRejected = computed(() => {
  if (totalReviewedCount.value === 0) {
    return "N/A";
  }
  const percentage = rejectedSuggestionsCount.value / totalReviewedCount.value;
  return `${Math.round(percentage * 1000) / 10}%`;
});

const agentsPercentPermissionGranted = computed(() => {
  if (agentsEnrolledCount.value === 0) {
    return "N/A";
  }
  const percentage =
    agentsPermissionGrantedCount.value / agentsEnrolledCount.value;
  return `${Math.round(percentage * 1000) / 10}%`;
});

const avgReviewTime = computed(() => {
  const seconds = avgReviewTimeSeconds.value;
  if (seconds === null) {
    return;
  }
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return { days, hours, minutes };
});

function getUrlSearchParams() {
  const params = new URLSearchParams({
    start: start.value,
    end: end.value,
  });
  if (props.domain) {
    params.append("domain", props.domain);
  }
  return params.toString();
}

async function getUpdatedWebsiteCount() {
  updatedWebsiteCountLoading.value = true;
  const params = getUrlSearchParams();
  const response = await fetch(
    `/api/cmp/${vendor.value}/stats/updated_domains_count/?${params.toString()}`,
  );
  updatedWebsiteCount.value = await response.json();
  updatedWebsiteCountLoading.value = false;
}

async function getStatusCounts() {
  statusCountsLoading.value = true;
  const params = getUrlSearchParams();
  const response = await fetch(
    `/api/cmp/${vendor.value}/stats/status_counts/?${params.toString()}`,
  );
  const counts = await response.json();
  pendingSuggestionsCount.value = counts.pending_count;
  createdSuggestionsCount.value = counts.created_count;
  uneditedAcceptedSuggestionsCount.value = counts.unedited_accepted_count;
  editedAcceptedSuggestionsCount.value = counts.edited_accepted_count;
  rejectedSuggestionsCount.value = counts.rejected_count;
  statusCountsLoading.value = false;
}

async function getAgentCounts() {
  agentCountsLoading.value = true;
  const response = await fetch(`/api/cmp/${vendor.value}/stats/agent_counts/`);
  const counts = await response.json();
  agentsEnrolledCount.value = counts.enrolled;
  agentsPermissionGrantedCount.value = counts.permission_granted;
  agentCountsLoading.value = false;
}

async function getAvgReviewTimeSeconds() {
  avgReviewTimeLoading.value = true;
  const params = getUrlSearchParams();
  const response = await fetch(
    `/api/cmp/${vendor.value}/stats/average_review_timedelta/?${params.toString()}`,
  );
  const responseText = await response.text();
  avgReviewTimeSeconds.value = responseText ? JSON.parse(responseText) : 0;
  avgReviewTimeLoading.value = false;
}

async function getRejectReasons() {
  rejectReasonsLoading.value = true;
  const params = getUrlSearchParams();
  const response = await fetch(
    `/api/cmp/${vendor.value}/stats/reject_reasons/?${params.toString()}`,
  );
  rejectReasons.value = await response.json();
  rejectReasonsLoading.value = false;
}

async function getHistoryChartData() {
  historyChartDataLoading.value = true;
  const params = getUrlSearchParams();
  const response = await fetch(
    `/api/cmp/${vendor.value}/stats/history_chart_data/?${params.toString()}`,
  );
  historyChartData.value = await response.json();
  historyChartDataLoading.value = false;
}

async function updateStats() {
  getUpdatedWebsiteCount();
  getStatusCounts();
  getAgentCounts();
  getAvgReviewTimeSeconds();
  getRejectReasons();
  getHistoryChartData();
}

watch([start, end], async ([_newStart, _newEnd], [_oldStart, _oldEnd]) => {
  updateStats();
});
</script>
